// ─── CostsSlaDashboardView — dashboard Custos & SLA Petrotec AO ──────────
//
// Mostra agregados (período) + tabela detalhada por OT com tempos e custos.
// Cada linha expande para timeline visual dos marcos cronológicos.
//
// Preços (€/km, €/h) vêm dos AccountingCode (700200A08* km, 700100A09 h)
// como defaults mas o operador pode override no dashboard para simular.

import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, IconButton, Tooltip, Chip,
  CircularProgress, Select, MenuItem, FormControl, InputLabel,
  Switch, FormControlLabel,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import RefreshIcon from '@material-ui/icons/Refresh';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import AssessmentIcon from '@material-ui/icons/Assessment';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import GetAppIcon from '@material-ui/icons/GetApp';
import { useDispatch } from 'react-redux';
import { setActiveView } from '../../store/slices/uiSlice';
import { AuthContext } from '../../App';

const INTERVENTION_LABELS = {
  accident:   { pt: 'P/Acidente',  color: '#dc2626' },
  eventual:   { pt: 'Eventual',    color: '#f59e0b' },
  corrective: { pt: 'Correctiva',  color: '#94a3b8' },
  preventive: { pt: 'Preventiva',  color: '#0ea5e9' },
  wear:       { pt: 'P/Desgaste',  color: '#a855f7' },
};

const SLA_STATUS = {
  met:            { label: 'Cumprido',    color: '#166534', bg: '#dcfce7' },
  missed:         { label: 'Falhado',     color: '#991b1b', bg: '#fee2e2' },
  running:        { label: 'Em curso',    color: '#1d4ed8', bg: '#dbeafe' },
  pending_start:  { label: 'Por iniciar', color: '#92400e', bg: '#fef3c7' },
  no_sla:         { label: 'Sem SLA',     color: '#475569', bg: '#f1f5f9' },
};

function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}
function fmtTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });
}
function fmtMinutes(min) {
  if (min == null) return '—';
  const m = Math.round(min);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r === 0 ? `${h}h` : `${h}h${String(r).padStart(2, '0')}`;
}
function fmtMoney(v) {
  if (v == null || isNaN(Number(v))) return '—';
  return Number(v).toLocaleString('pt-PT', { maximumFractionDigits: 0 });
}

const useStyles = makeStyles(() => ({
  root: { width: '100%', height: '100%', display: 'flex', flexDirection: 'column', background: '#f8fafc', overflow: 'hidden' },
  vatToggleLabel: { fontSize: '0.72rem', fontWeight: 600 },
  header: {
    display: 'flex', alignItems: 'center', gap: 12, padding: '16px 24px',
    background: '#fff', borderBottom: '1px solid #e2e8f0',
  },
  titleBox: { flex: 1, minWidth: 0 },
  title: { fontSize: '1.05rem', fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 },
  subtitle: { fontSize: '0.7rem', color: '#64748b', marginTop: 2 },
  filterBar: {
    padding: '12px 24px', background: '#fff', borderBottom: '1px solid #f1f5f9',
    display: 'grid', gridTemplateColumns: 'auto auto auto auto 1fr', gap: 10, alignItems: 'center',
  },
  priceBar: {
    padding: '10px 24px', background: '#eff6ff', borderBottom: '1px solid #dbeafe',
    display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
  },
  priceInput: {
    width: 130,
    '& .MuiOutlinedInput-root': { background: '#fff', borderRadius: 8, fontSize: '0.78rem' },
  },
  content: { flex: 1, overflowY: 'auto', padding: '16px 24px' },
  cardsRow: {
    display: 'grid', gap: 10, marginBottom: 16,
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  },
  statCard: {
    background: '#fff', borderRadius: 12, padding: '14px 18px',
    border: '1px solid #e2e8f0',
    display: 'flex', flexDirection: 'column', gap: 4,
  },
  statLabel: { fontSize: '0.66rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' },
  statValue: { fontSize: '1.1rem', fontWeight: 800, color: '#0f172a' },
  statHint: { fontSize: '0.62rem', color: '#94a3b8' },
  totalCard: {
    background: 'linear-gradient(135deg, #16a34a, #15803d)', color: '#fff',
    borderRadius: 12, padding: '16px 20px', marginBottom: 16,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  },
  table: {
    background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden',
  },
  tableHeader: {
    display: 'grid', gridTemplateColumns: '160px 1fr 100px 90px 80px 70px 80px 80px 80px 90px 24px',
    gap: 8, padding: '10px 14px', background: '#f8fafc',
    borderBottom: '2px solid #e2e8f0',
    fontSize: '0.6rem', fontWeight: 800, color: '#64748b',
    textTransform: 'uppercase', letterSpacing: '0.04em',
  },
  tableRow: {
    display: 'grid', gridTemplateColumns: '160px 1fr 100px 90px 80px 70px 80px 80px 80px 90px 24px',
    gap: 8, padding: '12px 14px',
    borderBottom: '1px solid #f1f5f9', alignItems: 'center',
    cursor: 'pointer',
    '&:hover': { background: '#fafbfc' },
  },
  expandedRow: {
    padding: '16px 18px', background: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
  },
  timeline: {
    position: 'relative', padding: '20px 16px',
    background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0',
  },
  timelineStops: {
    display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0,
    position: 'relative', alignItems: 'center',
  },
  timelineStop: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
    position: 'relative', zIndex: 2,
  },
  timelineDot: {
    width: 24, height: 24, borderRadius: '50%', background: '#3b82f6',
    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '0.6rem', fontWeight: 800,
  },
  timelineDotEmpty: {
    width: 24, height: 24, borderRadius: '50%',
    border: '2px dashed #cbd5e1', background: '#fff',
  },
  timelineLine: {
    position: 'absolute', top: 11, left: '7%', right: '7%', height: 2,
    background: '#cbd5e1', zIndex: 1,
  },
  timelineLabel: { fontSize: '0.6rem', fontWeight: 700, color: '#0f172a', textAlign: 'center', marginTop: 4 },
  timelineTime: { fontSize: '0.6rem', color: '#64748b' },
  intervalBox: {
    fontSize: '0.6rem', color: '#475569',
    background: '#fef3c7', border: '1px solid #fde68a',
    borderRadius: 6, padding: '2px 6px', marginTop: 4,
  },
  empty: {
    padding: '80px 24px', textAlign: 'center', color: '#94a3b8',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14,
  },
}));

function CostsSlaDashboardView({ lang = 'pt' }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { token, clientSettings } = useContext(AuthContext);
  const pt = lang === 'pt';
  const tenantCurrency = clientSettings?.defaultCurrency || 'EUR';

  const [from, setFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterSla, setFilterSla] = useState('');
  // Toggle para mostrar IVA — Petrotec AO usa 14%. Por defeito off
  // porque o dashboard é um cockpit interno; o BM PDF inclui sempre IVA.
  const [showVat, setShowVat] = useState(false);
  const VAT_RATE = 0.14;
  const [customers, setCustomers] = useState([]);

  const [pricePerKm, setPricePerKm] = useState('');
  const [pricePerHourHN, setPricePerHourHN] = useState('');
  const [pricePerHourHE, setPricePerHourHE] = useState('');

  // Estado inicial usa a moeda do tenant em vez de EUR hardcoded —
  // o banner do título e os labels não piscam "EUR" enquanto o fetch
  // está em curso ou em caso de erro.
  const [data, setData] = useState({ currency: tenantCurrency, prices: {}, defaults: {}, aggregates: {}, rows: [] });
  const currency = data.currency || tenantCurrency;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    fetch('/planner-api/final-customers', { headers })
      .then(r => r.ok ? r.json() : [])
      .then(d => setCustomers(Array.isArray(d) ? d : []))
      .catch(() => setCustomers([]));
  }, [token]);

  const fetchAll = useCallback(async () => {
    // Guarda explícita: o componente pode montar antes do AuthContext ter
    // populado o token. Sem isto a 1ª chamada saía com `Bearer undefined`
    // → backend devolvia 401 e o operador via "Não foi possível carregar".
    if (!token || typeof token !== 'string' || token.length < 10) return;
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      if (from) qs.set('from', from);
      if (to) qs.set('to', to);
      if (filterCustomer) qs.set('finalCustomerId', filterCustomer);
      if (filterType) qs.set('interventionType', filterType);
      if (pricePerKm !== '') qs.set('pricePerKm', pricePerKm);
      if (pricePerHourHN !== '') qs.set('pricePerHourHN', pricePerHourHN);
      if (pricePerHourHE !== '') qs.set('pricePerHourHE', pricePerHourHE);

      // headers construído DENTRO do callback — evita stale closure caso
      // o token mude (logout + login) e o useCallback ainda não tenha
      // sido recriado.
      const r = await fetch(`/planner-api/reports/costs-sla?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) {
        // Tornar o erro útil para o operador — vê o código HTTP exacto
        // em vez de uma mensagem genérica.
        const body = await r.text().catch(() => '');
        throw new Error(`HTTP ${r.status} ${body.slice(0, 80)}`);
      }
      const d = await r.json();
      setData(d);
      // sincronizar inputs com defaults se ainda vazios
      if (pricePerKm === '') setPricePerKm(String(d.defaults?.pricePerKm || 0));
      if (pricePerHourHN === '') setPricePerHourHN(String(d.defaults?.pricePerHourHN || 0));
      if (pricePerHourHE === '') setPricePerHourHE(String(d.defaults?.pricePerHourHE || 0));
    } catch (e) {
      setError(`${pt ? 'Não foi possível carregar' : 'Could not load'}: ${e.message}`);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, from, to, filterCustomer, filterType, pricePerKm, pricePerHourHN, pricePerHourHE, pt]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Filtro de SLA é local (sem ir ao backend)
  const filteredRows = data.rows.filter(r => {
    if (!filterSla) return true;
    return r.slaStatus === filterSla;
  });

  // Recalcular agregados se filtro local activo
  const agg = filterSla
    ? {
        orderCount: filteredRows.length,
        totalKm: filteredRows.reduce((s, r) => s + (r.distanceKm || 0), 0),
        totalWorkingMin: filteredRows.reduce((s, r) => s + (r.workingMin || 0), 0),
        totalHoursHN: filteredRows.reduce((s, r) => s + (r.hoursHN || 0), 0),
        totalHoursHE: filteredRows.reduce((s, r) => s + (r.hoursHE || 0), 0),
        totalCostTravel: filteredRows.reduce((s, r) => s + (r.costTravel || 0), 0),
        totalCostService: filteredRows.reduce((s, r) => s + (r.costService || 0), 0),
        totalCostMaterials: filteredRows.reduce((s, r) => s + (r.costMaterials || 0), 0),
        totalCost: filteredRows.reduce((s, r) => s + (r.costTotal || 0), 0),
        slaMetCount: filteredRows.filter(r => r.slaStatus === 'met').length,
        slaMissedCount: filteredRows.filter(r => r.slaStatus === 'missed').length,
      }
    : (data.aggregates || {});

  function exportCsv() {
    const headers = ['OT', 'Cliente', 'Posto', 'Tipo', 'Criada', 'Tempo SLA', 'SLA',
      'Km', 'Tempo serviço', 'H.N. (h)', 'H.E. (h)',
      `Custo deslocação (${currency})`, `Custo intervenção (${currency})`, `Custo materiais (${currency})`, `Total (${currency})`];
    const rows = filteredRows.map(r => [
      r.orderNumber || `OT-${r.id}`,
      r.finalCustomerName || '',
      r.siteName || '',
      INTERVENTION_LABELS[r.interventionType]?.pt || r.interventionType || '',
      fmtDateTime(r.createdAt),
      fmtMinutes(r.slaMin),
      SLA_STATUS[r.slaStatus]?.label || '',
      r.distanceKm || 0,
      fmtMinutes(r.workingMin),
      (r.hoursHN || 0).toFixed(2),
      (r.hoursHE || 0).toFixed(2),
      Math.round(r.costTravel),
      Math.round(r.costService),
      Math.round(r.costMaterials),
      Math.round(r.costTotal),
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `custos-sla_${from}_${to}.csv`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Tooltip title={pt ? 'Voltar a Manutenção' : 'Back'}>
          <IconButton size="small" onClick={() => dispatch(setActiveView('maintenance'))}>
            <ArrowBackIcon style={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
        <Box className={classes.titleBox}>
          <Typography className={classes.title}>
            <AssessmentIcon style={{ fontSize: 20, color: '#16a34a' }} />
            {pt ? 'Custos & SLA' : 'Costs & SLA'}
          </Typography>
          <Typography className={classes.subtitle}>
            {pt ? 'Custos por OT (km × valor/km · horas × valor/hora · materiais) + cumprimento de SLA' : 'Costs per OT + SLA compliance'}
          </Typography>
        </Box>
        <Button onClick={exportCsv} startIcon={<GetAppIcon style={{ fontSize: 16 }} />}
          style={{ textTransform: 'none', fontSize: '0.74rem', color: '#475569', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 14px' }}>
          {pt ? 'Exportar CSV' : 'Export CSV'}
        </Button>
        <IconButton size="small" onClick={fetchAll} disabled={loading}>
          <RefreshIcon style={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <Box className={classes.filterBar}>
        <TextField label={pt ? 'De' : 'From'} type="date" size="small" variant="outlined"
          value={from} onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }}
          style={{ width: 150 }} />
        <TextField label={pt ? 'Até' : 'To'} type="date" size="small" variant="outlined"
          value={to} onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }}
          style={{ width: 150 }} />
        <FormControl size="small" variant="outlined" style={{ minWidth: 180 }}>
          <InputLabel>{pt ? 'Cliente final' : 'Customer'}</InputLabel>
          <Select label={pt ? 'Cliente final' : 'Customer'} value={filterCustomer}
            onChange={e => setFilterCustomer(e.target.value)}>
            <MenuItem value="">{pt ? 'Todos' : 'All'}</MenuItem>
            {customers.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" variant="outlined" style={{ minWidth: 130 }}>
          <InputLabel>{pt ? 'Tipo' : 'Type'}</InputLabel>
          <Select label={pt ? 'Tipo' : 'Type'} value={filterType}
            onChange={e => setFilterType(e.target.value)}>
            <MenuItem value="">{pt ? 'Todos' : 'All'}</MenuItem>
            <MenuItem value="accident">P/Acidente</MenuItem>
            <MenuItem value="eventual">Eventual</MenuItem>
            <MenuItem value="preventive">Preventiva</MenuItem>
            <MenuItem value="wear">P/Desgaste</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" variant="outlined" style={{ minWidth: 130 }}>
          <InputLabel>{pt ? 'Estado SLA' : 'SLA'}</InputLabel>
          <Select label={pt ? 'Estado SLA' : 'SLA'} value={filterSla}
            onChange={e => setFilterSla(e.target.value)}>
            <MenuItem value="">{pt ? 'Todos' : 'All'}</MenuItem>
            <MenuItem value="met">Cumprido</MenuItem>
            <MenuItem value="missed">Falhado</MenuItem>
            <MenuItem value="running">Em curso</MenuItem>
            <MenuItem value="pending_start">Por iniciar</MenuItem>
            <MenuItem value="no_sla">Sem SLA</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box className={classes.priceBar}>
        <Typography style={{ fontSize: '0.72rem', fontWeight: 700, color: '#1e3a8a' }}>
          {pt ? `💰 Preços em ${currency} (defaults dos Códigos Contabilísticos — podes simular):` : `💰 Prices in ${currency}:`}
        </Typography>
        <TextField className={classes.priceInput} size="small" variant="outlined"
          label={pt ? 'Preço/km' : 'Price/km'} type="number"
          value={pricePerKm} onChange={e => setPricePerKm(e.target.value)} />
        <TextField className={classes.priceInput} size="small" variant="outlined"
          label="H.N. /hora" type="number"
          value={pricePerHourHN} onChange={e => setPricePerHourHN(e.target.value)} />
        <TextField className={classes.priceInput} size="small" variant="outlined"
          label="H.E. /hora" type="number"
          value={pricePerHourHE} onChange={e => setPricePerHourHE(e.target.value)} />
        <Button onClick={() => {
          setPricePerKm(String(data.defaults?.pricePerKm || 0));
          setPricePerHourHN(String(data.defaults?.pricePerHourHN || 0));
          setPricePerHourHE(String(data.defaults?.pricePerHourHE || 0));
        }} style={{ fontSize: '0.66rem', textTransform: 'none', color: '#1d4ed8' }}>
          {pt ? '↺ Repor defaults' : '↺ Reset'}
        </Button>
      </Box>

      <Box className={classes.content}>
        {error && (
          <Box style={{
            padding: '10px 14px', background: '#fef2f2',
            border: '1px solid #fecaca', color: '#991b1b',
            borderRadius: 8, marginBottom: 12, fontSize: '0.78rem',
          }}>{error}</Box>
        )}

        <Box className={classes.cardsRow}>
          <Box className={classes.statCard}>
            <Typography className={classes.statLabel}>{pt ? 'OTs no período' : 'Orders'}</Typography>
            <Typography className={classes.statValue}>{agg.orderCount || 0}</Typography>
          </Box>
          <Box className={classes.statCard}>
            <Typography className={classes.statLabel}>{pt ? 'Total km' : 'Total km'}</Typography>
            <Typography className={classes.statValue}>{(agg.totalKm || 0).toLocaleString('pt-PT', { maximumFractionDigits: 0 })}</Typography>
            <Typography className={classes.statHint}>× {fmtMoney(pricePerKm)} {currency}/km</Typography>
          </Box>
          <Box className={classes.statCard}>
            <Typography className={classes.statLabel}>{pt ? 'Horas trabalhadas' : 'Working hours'}</Typography>
            <Typography className={classes.statValue}>{fmtMinutes(agg.totalWorkingMin)}</Typography>
            {/* Divisão H.N./H.E. — calculada a partir do workSchedule
                de cada cliente final + feriados Angola. Chips coloridos
                para o operador identificar rapidamente. */}
            <Box style={{ display: 'flex', gap: 4, marginTop: 4 }}>
              <Chip
                label={`H.N. ${(agg.totalHoursHN || 0).toFixed(1)}h`}
                size="small"
                style={{ height: 18, fontSize: '0.6rem', fontWeight: 700, background: '#dcfce7', color: '#166534' }}
              />
              <Chip
                label={`H.E. ${(agg.totalHoursHE || 0).toFixed(1)}h`}
                size="small"
                style={{ height: 18, fontSize: '0.6rem', fontWeight: 700, background: '#fef3c7', color: '#92400e' }}
              />
            </Box>
          </Box>
          <Box className={classes.statCard}>
            <Typography className={classes.statLabel}>{pt ? 'Custo deslocação' : 'Travel cost'}</Typography>
            <Typography className={classes.statValue}>{fmtMoney(agg.totalCostTravel)} {currency}</Typography>
          </Box>
          <Box className={classes.statCard}>
            <Typography className={classes.statLabel}>{pt ? 'Custo mão de obra' : 'Labor cost'}</Typography>
            <Typography className={classes.statValue}>{fmtMoney(agg.totalCostService)} {currency}</Typography>
          </Box>
          <Box className={classes.statCard}>
            <Typography className={classes.statLabel}>{pt ? 'Custo materiais' : 'Materials cost'}</Typography>
            <Typography className={classes.statValue}>{fmtMoney(agg.totalCostMaterials)} {currency}</Typography>
          </Box>
        </Box>

        <Box className={classes.totalCard}>
          <Box style={{ flex: 1 }}>
            <Typography style={{ fontSize: '0.66rem', fontWeight: 700, opacity: 0.85, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {pt ? 'Total facturável estimado' : 'Estimated billable total'}
            </Typography>
            {showVat ? (
              <Box style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginTop: 2, flexWrap: 'wrap' }}>
                <Box>
                  <Typography style={{ fontSize: '0.6rem', opacity: 0.8 }}>{pt ? 'Subtotal' : 'Subtotal'}</Typography>
                  <Typography style={{ fontSize: '1.1rem', fontWeight: 700 }}>{fmtMoney(agg.totalCost)}</Typography>
                </Box>
                <Box>
                  <Typography style={{ fontSize: '0.6rem', opacity: 0.8 }}>{pt ? `IVA (${(VAT_RATE * 100).toFixed(0)}%)` : `VAT (${(VAT_RATE * 100).toFixed(0)}%)`}</Typography>
                  <Typography style={{ fontSize: '1.1rem', fontWeight: 700 }}>{fmtMoney(agg.totalCost * VAT_RATE)}</Typography>
                </Box>
                <Box>
                  <Typography style={{ fontSize: '0.6rem', opacity: 0.85, fontWeight: 700 }}>{pt ? 'Total c/IVA' : 'Total w/VAT'}</Typography>
                  <Typography style={{ fontSize: '1.8rem', fontWeight: 800 }}>
                    {fmtMoney(agg.totalCost * (1 + VAT_RATE))} <span style={{ fontSize: '1rem', opacity: 0.9 }}>{currency}</span>
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Typography style={{ fontSize: '1.8rem', fontWeight: 800, marginTop: 2 }}>
                {fmtMoney(agg.totalCost)} <span style={{ fontSize: '1rem', opacity: 0.9 }}>{currency}</span>
              </Typography>
            )}
            <FormControlLabel
              control={<Switch size="small" checked={showVat} onChange={(e) => setShowVat(e.target.checked)} />}
              label={pt ? `Mostrar IVA (${(VAT_RATE * 100).toFixed(0)}%)` : `Show VAT (${(VAT_RATE * 100).toFixed(0)}%)`}
              style={{ marginTop: 6, color: 'rgba(255,255,255,0.85)' }}
              classes={{ label: classes.vatToggleLabel }}
            />
          </Box>
          <Box style={{ textAlign: 'right' }}>
            <Typography style={{ fontSize: '0.66rem', opacity: 0.85 }}>
              {pt ? 'SLA cumprido' : 'SLA met'}
            </Typography>
            <Typography style={{ fontSize: '1.2rem', fontWeight: 700 }}>
              {agg.slaMetCount || 0}{agg.slaMissedCount > 0 ? ` · ${agg.slaMissedCount} falhado` : ''}
            </Typography>
          </Box>
        </Box>

        {loading && filteredRows.length === 0 ? (
          <Box className={classes.empty}>
            <CircularProgress size={32} style={{ color: '#16a34a' }} />
            <Typography style={{ fontSize: '0.8rem' }}>{pt ? 'A carregar…' : 'Loading…'}</Typography>
          </Box>
        ) : filteredRows.length === 0 ? (
          <Box className={classes.empty}>
            <AssessmentIcon style={{ fontSize: 48, color: '#cbd5e1' }} />
            <Typography style={{ fontSize: '0.85rem', fontWeight: 600 }}>
              {pt ? 'Sem OTs no período seleccionado.' : 'No orders in period.'}
            </Typography>
          </Box>
        ) : (
          <Box className={classes.table}>
            <Box className={classes.tableHeader}>
              <span>OT</span>
              <span>Cliente / Posto</span>
              <span>Tipo</span>
              <span>Criada</span>
              <span style={{ textAlign: 'right' }}>Km</span>
              <span style={{ textAlign: 'right' }}>Tempo</span>
              <span style={{ textAlign: 'right' }}>Desloc. ({currency})</span>
              <span style={{ textAlign: 'right' }}>Mão obra ({currency})</span>
              <span style={{ textAlign: 'right' }}>Mater. ({currency})</span>
              <span style={{ textAlign: 'right' }}>Total ({currency})</span>
              <span></span>
            </Box>
            {filteredRows.map(r => {
              const tMeta = INTERVENTION_LABELS[r.interventionType] || INTERVENTION_LABELS.eventual;
              const sMeta = SLA_STATUS[r.slaStatus] || SLA_STATUS.no_sla;
              const isExp = expanded === r.id;
              return (
                <React.Fragment key={r.id}>
                  <Box className={classes.tableRow}
                    onClick={() => setExpanded(isExp ? null : r.id)}>
                    <Box>
                      <Typography style={{ fontSize: '0.78rem', fontWeight: 800, color: '#0f172a' }}>
                        {r.orderNumber || `OT-${r.id}`}
                      </Typography>
                      <Chip label={sMeta.label} size="small"
                        style={{ height: 14, fontSize: '0.55rem', fontWeight: 700, marginTop: 2,
                          background: sMeta.bg, color: sMeta.color }} />
                    </Box>
                    <Box>
                      <Typography style={{ fontSize: '0.78rem', fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.finalCustomerName || '—'}
                      </Typography>
                      <Typography style={{ fontSize: '0.66rem', color: '#64748b' }}>
                        {r.siteName || '—'}{r.vehiclePlate ? ` · ${r.vehiclePlate}` : ''}
                      </Typography>
                    </Box>
                    <Chip label={tMeta.pt} size="small"
                      style={{ height: 18, fontSize: '0.6rem', fontWeight: 700,
                        background: `${tMeta.color}1A`, color: tMeta.color }} />
                    <Typography style={{ fontSize: '0.7rem', color: '#475569' }}>
                      {fmtDateTime(r.createdAt)}
                    </Typography>
                    <Typography style={{ fontSize: '0.78rem', fontWeight: 700, textAlign: 'right' }}>
                      {r.distanceKm > 0 ? r.distanceKm : '—'}
                    </Typography>
                    <Box style={{ textAlign: 'right' }}>
                      <Typography style={{ fontSize: '0.78rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.1 }}>
                        {fmtMinutes(r.workingMin)}
                      </Typography>
                      {(r.hoursHN > 0 || r.hoursHE > 0) && (
                        <Typography style={{ fontSize: '0.58rem', color: '#64748b', marginTop: 1 }}>
                          <span style={{ color: '#166534', fontWeight: 700 }}>N {r.hoursHN.toFixed(1)}h</span>
                          {' · '}
                          <span style={{ color: '#92400e', fontWeight: 700 }}>E {r.hoursHE.toFixed(1)}h</span>
                        </Typography>
                      )}
                    </Box>
                    <Typography style={{ fontSize: '0.78rem', fontWeight: 700, textAlign: 'right', color: '#475569' }}>
                      {fmtMoney(r.costTravel)}
                    </Typography>
                    <Typography style={{ fontSize: '0.78rem', fontWeight: 700, textAlign: 'right', color: '#475569' }}>
                      {fmtMoney(r.costService)}
                    </Typography>
                    <Typography style={{ fontSize: '0.78rem', fontWeight: 700, textAlign: 'right', color: '#475569' }}>
                      {fmtMoney(r.costMaterials)}
                    </Typography>
                    <Typography style={{ fontSize: '0.84rem', fontWeight: 800, textAlign: 'right', color: '#16a34a' }}>
                      {fmtMoney(r.costTotal)}
                    </Typography>
                    <IconButton size="small" style={{ padding: 0 }}>
                      {isExp ? <ExpandLessIcon style={{ fontSize: 18 }} /> : <ExpandMoreIcon style={{ fontSize: 18 }} />}
                    </IconButton>
                  </Box>

                  {isExp && (
                    <Box className={classes.expandedRow}>
                      <Typography style={{ fontSize: '0.74rem', fontWeight: 700, color: '#475569', marginBottom: 8 }}>
                        {pt ? 'Cronologia da OT' : 'Order timeline'} · SLA limite: {r.slaHours != null ? (
                          <>
                            {r.slaHours}h
                            {r.slaSource === 'contract' && (
                              <span style={{ marginLeft: 4, fontSize: '0.62rem', fontWeight: 600, color: '#0369a1', background: '#e0f2fe', padding: '1px 6px', borderRadius: 4 }}>
                                {pt ? 'do contrato' : 'from contract'}
                              </span>
                            )}
                          </>
                        ) : '—'} · SLA decorrido: <b style={{ color: sMeta.color }}>{fmtMinutes(r.slaMin)}</b>
                      </Typography>
                      <Box className={classes.timeline}>
                        <Box className={classes.timelineLine} />
                        <Box className={classes.timelineStops}>
                          {[
                            { label: pt ? 'Ordem Criada' : 'Order Created', time: r.createdAt, icon: '📝' },
                            { label: pt ? 'Saída da base' : 'Departed base', time: r.departedBaseAt, icon: '🏢' },
                            { label: pt ? 'Chegada ao local' : 'Arrived on site', time: r.arrivedAt, icon: '📍' },
                            { label: pt ? 'Serviço iniciado' : 'Service started', time: r.serviceStartedAt, icon: '▶' },
                            { label: pt ? 'Serviço Terminado' : 'Service finished', time: r.completedAt, icon: '✓' },
                            { label: pt ? 'Em regresso' : 'Returning', time: r.returnedBaseAt, icon: '🏠' },
                            { label: pt ? 'Concluída' : 'Completed', time: r.deliveredAt, icon: '📦' },
                          ].map((step, idx) => (
                            <Box key={idx} className={classes.timelineStop}>
                              <Box className={step.time ? classes.timelineDot : classes.timelineDotEmpty}>
                                {step.time && step.icon}
                              </Box>
                              <Typography className={classes.timelineLabel}>{step.label}</Typography>
                              <Typography className={classes.timelineTime}>{fmtTime(step.time)}</Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                      <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginTop: 10 }}>
                        <Box style={{ background: '#dbeafe', padding: '8px 10px', borderRadius: 6 }}>
                          <Typography style={{ fontSize: '0.6rem', color: '#1e40af', fontWeight: 700 }}>{pt ? 'Deslocação ida' : 'Going'}</Typography>
                          <Typography style={{ fontSize: '0.86rem', fontWeight: 800, color: '#1e3a8a' }}>{fmtMinutes(r.goingMin)}</Typography>
                        </Box>
                        <Box style={{ background: '#fef3c7', padding: '8px 10px', borderRadius: 6 }}>
                          <Typography style={{ fontSize: '0.6rem', color: '#92400e', fontWeight: 700 }}>{pt ? 'Espera no posto' : 'Waiting'}</Typography>
                          <Typography style={{ fontSize: '0.86rem', fontWeight: 800, color: '#78350f' }}>{fmtMinutes(r.waitingMin)}</Typography>
                        </Box>
                        <Box style={{ background: '#dcfce7', padding: '8px 10px', borderRadius: 6 }}>
                          <Typography style={{ fontSize: '0.6rem', color: '#166534', fontWeight: 700 }}>{pt ? 'Tempo facturável' : 'Billable time'}</Typography>
                          <Typography style={{ fontSize: '0.86rem', fontWeight: 800, color: '#14532d' }}>{fmtMinutes(r.workingMin)}</Typography>
                          {(r.hoursHN > 0 || r.hoursHE > 0) && (
                            <Typography style={{ fontSize: '0.58rem', color: '#166534', marginTop: 2 }}>
                              H.N. {r.hoursHN.toFixed(1)}h · H.E. {r.hoursHE.toFixed(1)}h
                            </Typography>
                          )}
                        </Box>
                        <Box style={{ background: '#e0e7ff', padding: '8px 10px', borderRadius: 6 }}>
                          <Typography style={{ fontSize: '0.6rem', color: '#3730a3', fontWeight: 700 }}>{pt ? 'Regresso' : 'Return'}</Typography>
                          <Typography style={{ fontSize: '0.86rem', fontWeight: 800, color: '#312e81' }}>{fmtMinutes(r.returningMin)}</Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </React.Fragment>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default CostsSlaDashboardView;
